import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LineField } from "../components/LineField";
import { VerifierPicker } from "../components/VerifierPicker";
import { EmptyState } from "../components/EmptyState";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePrice } from "../hooks/usePrice";
import { useT } from "../i18n/LanguageProvider";
import { listCredentials } from "../api/credentials";
import { C, font } from "../styles/tokens";
import { formatRupees } from "../utils/money";

const VERIFIER_SHARE_PAISE = 12500; // Rs. 125 to verifier
const PLATFORM_SHARE_PAISE = 37500; // Rs. 375 to platform

export function RequestVerificationPage() {
  const isMobile = useIsMobile();
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCredentialId = searchParams.get("credentialId") ?? "";

  const [credentials, setCredentials] = useState([]);
  const [selectedCredentialId, setSelectedCredentialId] =
    useState(initialCredentialId);
  const [verifierType, setVerifierType] = useState("INSTITUTION");
  const [selectedVerifier, setSelectedVerifier] = useState(null);

  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const items = await listCredentials();
        const eligible = items.filter(
          (c) => c.status === "NONE" || c.status === "REJECTED",
        );
        setCredentials(eligible);
      } catch (err) {
        setError(err.message ?? t("common.somethingWrong"));
      } finally {
        setLoadingCredentials(false);
      }
    })();
  }, []);

  const selectedCredential = useMemo(
    () => credentials.find((c) => c.id === selectedCredentialId),
    [credentials, selectedCredentialId],
  );

  useEffect(() => {
    if (!selectedCredential) return;
    if (selectedCredential.type === "EDUCATION") setVerifierType("INSTITUTION");
    else if (selectedCredential.type === "WORK")
      setVerifierType("ORGANISATION");
  }, [selectedCredential]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!selectedCredentialId) {
      setError("Choose a credential to verify.");
      return;
    }
    if (!selectedVerifier) {
      setError("Choose a verifier.");
      return;
    }

    navigate("/checkout", {
      state: {
        type: "VERIFICATION_FEE",
        provider: "CASHFREE",
        credentialId: selectedCredentialId,
        verifierType,
        verifierId: selectedVerifier.id,
      },
    });
  }

  if (loadingCredentials) {
    return (
      <AppShell>
        <div style={{ color: C.textMed }}>{t("common.loading")}</div>
      </AppShell>
    );
  }

  if (credentials.length === 0) {
    return (
      <AppShell maxWidth={680}>
        <div style={cardStyle}>
          <EmptyState
            kind="check"
            title="All your credentials are accounted for"
            body="Add a new credential first, or check My verifications - any existing requests are tracked there."
            ctaLabel="Back to profile"
            onCtaClick={() => navigate("/profile")}
            size="large"
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={720}>
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 700,
            margin: "0 0 6px",
            letterSpacing: -0.4,
          }}
        >
          {t("verify.title")}
        </h1>
        <p
          style={{ fontSize: 14, color: C.textMed, margin: 0, lineHeight: 1.5 }}
        >
          {t("verify.subtitle")}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <StepCard
          step={1}
          title={t("verify.credentialToVerify")}
          isMobile={isMobile}
        >
          <LineField
            label=""
            as="select"
            placeholder={t("verify.chooseCredential")}
            value={selectedCredentialId}
            onChange={(e) => setSelectedCredentialId(e.target.value)}
            options={credentials.map((c) => ({
              value: c.id,
              label: `${labelType(c.type)} - ${c.title} (${c.issuer})`,
            }))}
            required
          />
        </StepCard>

        <StepCard step={2} title={t("verify.verifierType")} isMobile={isMobile}>
          <div
            data-responsive-grid="2"
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 12,
            }}
          >
            <RadioOption
              active={verifierType === "INSTITUTION"}
              onClick={() => setVerifierType("INSTITUTION")}
              label={t("verify.academicInstitution")}
              hint={t("verify.academicInstitutionHint")}
            />
            <RadioOption
              active={verifierType === "ORGANISATION"}
              onClick={() => setVerifierType("ORGANISATION")}
              label={t("verify.organisation")}
              hint={t("verify.organisationHint")}
            />
          </div>
        </StepCard>

        <StepCard
          step={3}
          isMobile={isMobile}
          title={
            verifierType === "INSTITUTION"
              ? t("verify.whichInstitution")
              : t("verify.whichOrganisation")
          }
        >
          <VerifierPicker
            verifierType={verifierType}
            value={selectedVerifier}
            onChange={setSelectedVerifier}
          />
        </StepCard>

        <PaymentSummary isMobile={isMobile} />

        {error && (
          <div
            style={{
              fontSize: 13,
              color: C.red,
              background: C.redLight,
              padding: "10px 12px",
              borderRadius: 4,
              marginTop: 12,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 12,
            marginTop: 28,
          }}
        >
          <button
            type="submit"
            style={{
              ...primaryButton,
              background: C.blue,
              cursor: "pointer",
              order: isMobile ? 1 : 2,
              flex: isMobile ? "none" : 2,
              width: isMobile ? "100%" : "auto",
              minHeight: 44,
            }}
          >
            Proceed to Checkout
          </button>
          <button
            type="button"
            onClick={() => navigate("/profile")}
            style={{
              ...cancelButton,
              order: isMobile ? 2 : 1,
              flex: isMobile ? "none" : 1,
              width: isMobile ? "100%" : "auto",
              minHeight: 44,
            }}
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </AppShell>
  );
}

// ---- subcomponents ----

function StepCard({ step, title, children, isMobile }) {
  return (
    <section
      style={{
        background: C.bg,
        border: `1px solid ${C.divider}`,
        borderRadius: 8,
        padding: isMobile ? 16 : 20,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: C.blueLight,
            color: C.blue,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {step}
        </span>
        <h2
          style={{
            fontSize: 14,
            fontWeight: 700,
            margin: 0,
            color: C.textDark,
          }}
        >
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function RadioOption({ active, onClick, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "14px 16px",
        textAlign: "left",
        background: active ? C.blueLight : C.bg,
        border: active
          ? `2px solid ${C.blue}`
          : `1.5px solid ${C.dividerStrong}`,
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: font,
        transition: "background 0.12s ease, border-color 0.12s ease",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: active ? C.blueDark : C.textDark,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 11, color: C.textMed }}>{hint}</div>
    </button>
  );
}

function PaymentSummary({ isMobile }) {
  // The fee the server charges, read before the order (as the app does).
  const fee = usePrice("VERIFICATION_FEE");
  const t = useT();
  const providerLabel = "Cashfree Payments";

  return (
    <section
      style={{
        background: C.bg,
        border: `1px solid ${C.divider}`,
        borderRadius: 8,
        padding: isMobile ? 16 : 22,
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h2
          style={{
            fontSize: 14,
            fontWeight: 700,
            margin: 0,
            color: C.textDark,
          }}
        >
          {t("verify.verificationFee")}
        </h2>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: C.textDark,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fee.pricePaise
            ? formatRupees(fee.pricePaise)
            : (fee.error ?? "Loading...")}
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: `1px solid ${C.divider}`,
          fontSize: 11,
          color: C.textLight,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Secured by {providerLabel}
      </div>
    </section>
  );
}

// ---- helpers ----

function labelType(t) {
  return { EDUCATION: "Education", WORK: "Work", OTHER: "Other" }[t] ?? t;
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};

const cancelButton = {
  flex: 1,
  padding: "12px",
  fontSize: 14,
  fontWeight: 600,
  color: C.textDark,
  background: "transparent",
  border: `1px solid ${C.dividerStrong}`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};

const primaryButton = {
  flex: 2,
  padding: "12px",
  fontSize: 14,
  fontWeight: 600,
  color: "white",
  border: 0,
  borderRadius: 4,
  fontFamily: font,
};
